import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import PurchaseOrderReceptionModal from '../suppliers/PurchaseOrderReceptionModal';
import PurchaseOrderModal from '../suppliers/PurchaseOrderModal';
import Pagination from '../../components/common/Pagination';
import PermissionGuard from '../../components/common/PermissionGuard';
import * as XLSX from 'xlsx';
import SortableHeader from '../../components/common/SortableHeader';

const getStatusStyle = (status) => {
    switch (status) {
        case 'Brouillon': return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
        case 'Envoyée': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
        case 'Partiellement Livrée': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
        case 'Livrée': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
        case 'Clôturée': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
        default: return 'bg-slate-100 text-slate-600';
    }
};

const PurchaseOrderList = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { showToast, showConfirm } = useUI();

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(parseInt(localStorage.getItem('app_items_per_page')) || 25);
    const [totalPages, setTotalPages] = useState(1);
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('DESC');

    // Filters
    const [statusFilter, setStatusFilter] = useState('Tous');
    const [supplierFilter, setSupplierFilter] = useState('Tous');
    const [searchQuery, setSearchQuery] = useState('');
    const [suppliersList, setSuppliersList] = useState([]);

    // Modals
    const [isReceptionModalOpen, setIsReceptionModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [supplierCatalogue, setSupplierCatalogue] = useState([]);
    const [isSupplierSelectModalOpen, setIsSupplierSelectModalOpen] = useState(false);

    const handleExport = () => {
        try {
            showToast("Génération de l'export des commandes...", "info");
            const exportData = orders.map(o => ({
                'Référence Système': o.reference,
                'Référence ERP': o.erpReference || '',
                'Référence Fournisseur': o.supplierReference || '',
                'Fournisseur': o.supplier?.name || 'Inconnu',
                'Date': new Date(o.createdAt).toLocaleDateString('fr-FR'),
                'Statut': o.status,
                'Total HT': o.totalHT,
                'Total TTC': o.totalTTC,
                'Articles': o.itemsCount || 0
            }));

            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Commandes Fournisseurs');

            const date = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(wb, `Export_Commandes_${date}.xlsx`);
            showToast("Export réussi", "success");
        } catch (error) {
            console.error('Export error:', error);
            showToast("Erreur lors de l'exportation", "error");
        }
    };

    const fetchOrders = useCallback(async () => {
        if (!user?.companyId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({
                companyId: user.companyId,
                page,
                limit,
                sortBy: sortBy,
                sortOrder: sortOrder
            });
            if (statusFilter !== 'Tous') params.append('status', statusFilter);
            if (supplierFilter !== 'Tous') params.append('supplierId', supplierFilter);
            if (searchQuery) params.append('search', searchQuery);

            const res = await fetch(`/api/purchase-orders?${params.toString()}`);
            if (res.ok) {
                const data = await res.json();
                setOrders(data.orders || []);
                setTotal(data.total || 0);
                setTotalPages(data.totalPages || 1);
            }
        } catch (error) {
            showToast("Erreur de chargement des commandes", "error");
        } finally {
            setLoading(false);
        }
    }, [user?.companyId, page, limit, statusFilter, supplierFilter, searchQuery, showToast, sortBy, sortOrder]);

    useEffect(() => {
        const fetchSuppliers = async () => {
            if (!user?.companyId) return;
            try {
                const res = await fetch(`/api/suppliers?companyId=${user.companyId}&limit=999`);
                if (res.ok) {
                    const data = await res.json();
                    setSuppliersList(data.suppliers || []);
                }
            } catch (err) { console.error(err); }
        };
        fetchSuppliers();
    }, [user?.companyId]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const handleEditOrder = async (orderInfo) => {
        try {
            // Load full order details, supplier and catalogue before opening modal
            const [orderRes, sRes, cRes] = await Promise.all([
                fetch(`/api/purchase-orders/${orderInfo.id}`),
                fetch(`/api/suppliers/${orderInfo.supplierId}`),
                fetch(`/api/suppliers/${orderInfo.supplierId}/products`)
            ]);

            if (orderRes.ok && sRes.ok && cRes.ok) {
                const fullOrder = await orderRes.json();
                const sData = await sRes.json();
                const cData = await cRes.json();
                setSelectedSupplier(sData);
                setSupplierCatalogue(cData);
                setSelectedOrder(fullOrder);
                setIsEditModalOpen(true);
            }
        } catch (err) {
            showToast("Erreur lors du chargement des données", "error");
        }
    };

    const handleRowClick = async (orderInfo) => {
        if (orderInfo.status === 'Brouillon') {
            handleEditOrder(orderInfo);
        } else {
            // Sent, Partial or Closed -> Reception Modal
            try {
                const res = await fetch(`/api/purchase-orders/${orderInfo.id}`);
                if (res.ok) {
                    const fullOrder = await res.json();
                    setSelectedOrder(fullOrder);
                    setIsReceptionModalOpen(true);
                } else {
                    showToast("Erreur lors du chargement de la commande", "error");
                }
            } catch (err) {
                showToast("Erreur serveur", "error");
            }
        }
    };

    const handleDownloadPDF = async (e, orderId, reference) => {
        e.stopPropagation();
        try {
            const res = await fetch(`/api/purchase-orders/${orderId}/pdf`);
            if (!res.ok) throw new Error("Erreur serveur");
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Commande_${reference}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            showToast("Erreur lors du téléchargement", "error");
        }
    };

    const handleDuplicate = async (e, orderId) => {
        e.stopPropagation();
        try {
            const res = await fetch(`/api/purchase-orders/${orderId}/duplicate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: user.id })
            });
            if (res.ok) {
                const newOrder = await res.json();
                showToast("Commande dupliquée en brouillon", "success");
                fetchOrders();
                // Automatically open edit modal for the new draft
                if (newOrder.supplierId) {
                    const [sRes, cRes] = await Promise.all([
                        fetch(`/api/suppliers/${newOrder.supplierId}`),
                        fetch(`/api/suppliers/${newOrder.supplierId}/products`)
                    ]);
                    if (sRes.ok && cRes.ok) {
                        const sData = await sRes.json();
                        const cData = await cRes.json();
                        setSelectedSupplier(sData);
                        setSupplierCatalogue(cData);
                        setSelectedOrder(newOrder);
                        setIsEditModalOpen(true);
                    }
                }
            } else {
                const data = await res.json();
                showToast(data.error || "Erreur de duplication", "error");
            }
        } catch (error) {
            showToast("Erreur serveur", "error");
        }
    };

    const handleMarkDelivered = async (e, orderId) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        console.log("handleMarkDelivered called for", orderId);

        showConfirm({
            title: 'Marquer comme livrée ?',
            message: 'Voulez-vous vraiment marquer cette commande comme livrée ? Cela notifiera les opérateurs pour la réception.',
            type: 'info',
            onConfirm: async () => {
                try {
                    const res = await fetch(`/api/purchase-orders/${orderId}/mark-delivered`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ userId: user.id })
                    });
                    if (res.ok) {
                        showToast("Commande marquée comme livrée", "success");
                        fetchOrders();
                    } else {
                        const data = await res.json();
                        showToast(data.error || "Erreur de mise à jour", "error");
                    }
                } catch (error) {
                    showToast("Erreur serveur", "error");
                }
            }
        });
    };

    const handleStartNewOrder = () => {
        if (supplierFilter !== 'Tous') {
            const supplier = suppliersList.find(s => s.id === parseInt(supplierFilter));
            if (supplier) {
                onSupplierSelected(supplier);
                return;
            }
        }
        setIsSupplierSelectModalOpen(true);
    };

    const onSupplierSelected = async (supplier) => {
        setIsSupplierSelectModalOpen(false);
        try {
            const res = await fetch(`/api/suppliers/${supplier.id}/products`);
            if (res.ok) {
                const products = await res.json();
                setSelectedSupplier(supplier);
                setSupplierCatalogue(products);
                setSelectedOrder(null);
                setIsEditModalOpen(true);
            }
        } catch (error) {
            showToast("Erreur lors du chargement du catalogue", "error");
        }
    };



    return (
        <div className="flex flex-col">
            {/* Header Section - Sticky with blur */}
            <div className="sticky top-[-16px] lg:top-[-16px] z-30 bg-background-light/95 dark:bg-background-dark/95 backdrop-blur-sm pt-4 pb-6 mb-2 border-b border-slate-200/50 dark:border-slate-800/50">
                <div className="flex flex-wrap justify-between items-end gap-4 max-w-[1600px] mx-auto w-full">
                    <div className="flex flex-col gap-1">
                        <h1 className="text-3xl font-black leading-tight tracking-tight flex items-center gap-3 bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent drop-shadow-sm">
                            Commandes Fournisseurs
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Gestion globale de tous vos approvisionnements ({total} commandes).</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={handleStartNewOrder}
                            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98]"
                        >
                            <span className="material-symbols-outlined text-lg">add_shopping_cart</span>
                            <span>Passe une commande</span>
                        </button>
                        <button
                            onClick={handleExport}
                            className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-sm"
                            title="Exporter Excel"
                        >
                            <span className="material-symbols-outlined text-lg">file_download</span>
                            <span className="hidden sm:inline">Exporter</span>
                        </button>
                        <button
                            onClick={fetchOrders}
                            className="size-10 flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-background-light transition-all active:scale-95 shadow-sm"
                        >
                            <span className="material-symbols-outlined text-slate-600 dark:text-slate-400">refresh</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="space-y-8 pt-4">
                {/* Filters & Table Container */}
                <div className="bg-white dark:bg-slate-900/50 rounded-3xl border border-slate-200 dark:border-slate-800 overflow-hidden backdrop-blur-sm shadow-xl shadow-slate-200/50 dark:shadow-none">
                    <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex flex-wrap gap-4 items-center justify-between bg-background-light/50 dark:bg-background-dark/30">
                        <div className="flex flex-wrap gap-3">
                            <div className="relative">
                                <select
                                    className="appearance-none h-10 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 pr-10 text-sm font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all cursor-pointer hover:border-primary/30"
                                    value={supplierFilter}
                                    onChange={(e) => { setSupplierFilter(e.target.value); setPage(1); }}
                                >
                                    <option value="Tous">Tous les fournisseurs</option>
                                    {suppliersList.map(s => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                                <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] pointer-events-none">expand_more</span>
                            </div>
                            <div className="relative">
                                <select
                                    className="appearance-none h-10 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 pr-10 text-sm font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all cursor-pointer hover:border-primary/30"
                                    value={statusFilter}
                                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                                >
                                    <option value="Tous">Tous les statuts</option>
                                    <option value="Brouillon">Brouillon</option>
                                    <option value="Envoyée">Envoyée</option>
                                    <option value="Livrée">Livrée</option>
                                    <option value="Partiellement Livrée">Partiellement Livrée</option>
                                    <option value="Clôturée">Clôturée</option>
                                </select>
                                <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] pointer-events-none">expand_more</span>
                            </div>
                        </div>
                        <div className="flex-1 max-w-md">
                            <div className="relative">
                                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">search</span>
                                <input
                                    type="text"
                                    placeholder="Rechercher une référence..."
                                    value={searchQuery}
                                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                                    className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto custom-scrollbar">
                        <table className="w-full text-left border-collapse min-w-[800px]">
                            <thead>
                                <tr className="bg-background-light/50 dark:bg-slate-800/50 text-slate-500 text-[10px] uppercase tracking-widest font-black border-b border-slate-200 dark:border-slate-800">
                                    <SortableHeader
                                        label="Réf / Date"
                                        field="createdAt"
                                        currentSort={sortBy}
                                        currentOrder={sortOrder}
                                        onSort={(f, o) => { setSortBy(f); setSortOrder(o); }}
                                        className="px-6 py-4 w-[180px]"
                                    />
                                    <SortableHeader
                                        label="Fournisseur"
                                        field="supplier.name"
                                        currentSort={sortBy}
                                        currentOrder={sortOrder}
                                        onSort={(f, o) => { setSortBy(f); setSortOrder(o); }}
                                        className="px-6 py-4 min-w-[200px]"
                                    />
                                    <SortableHeader
                                        label="Statut"
                                        field="status"
                                        currentSort={sortBy}
                                        currentOrder={sortOrder}
                                        onSort={(f, o) => { setSortBy(f); setSortOrder(o); }}
                                        className="px-6 py-4 w-[110px]"
                                    />
                                    <th className="px-6 py-4 min-w-[150px]">Créateur</th>

                                    <SortableHeader
                                        label="Montant"
                                        field="totalHT"
                                        currentSort={sortBy}
                                        currentOrder={sortOrder}
                                        onSort={(f, o) => { setSortBy(f); setSortOrder(o); }}
                                        className="px-6 py-4 w-[100px] text-right justify-end"
                                    />
                                    <PermissionGuard permission="order.view">
                                        <th className="px-6 py-4 text-center w-[180px]">Actions</th>
                                    </PermissionGuard>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="py-20 text-center">
                                            <div className="animate-spin size-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                                            <p className="text-slate-500 font-bold">Chargement des commandes...</p>
                                        </td>
                                    </tr>
                                ) : orders.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="py-20 text-center text-slate-400 italic">
                                            Aucune commande trouvée.
                                        </td>
                                    </tr>
                                ) : orders.map(order => (
                                    <tr
                                        key={order.id}
                                        onClick={() => handleRowClick(order)}
                                        className="group hover:bg-background-light dark:hover:bg-slate-800/40 transition-colors cursor-pointer"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <p className="font-bold text-sm text-slate-900 dark:text-white" title="Référence Système">{order.reference}</p>
                                                {order.erpReference && (
                                                    <p className="text-[10px] font-bold text-primary/80" title="Référence ERP">ERP: {order.erpReference}</p>
                                                )}
                                                {order.supplierReference && (
                                                    <p className="text-[10px] text-slate-400 italic truncate max-w-[150px]" title="Référence Fournisseur">Fourn: {order.supplierReference}</p>
                                                )}
                                            </div>
                                            <p className="text-[10px] text-slate-500 mt-1 font-mono uppercase">
                                                {new Date(order.orderDate).toLocaleDateString()}
                                            </p>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="font-bold text-sm text-slate-800 dark:text-slate-200">{order.supplier?.name}</p>
                                            <span className="text-[10px] text-slate-400 italic">{order.supplier?.category}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider whitespace-nowrap ${getStatusStyle(order.status)}`}>
                                                {order.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                {order.creator ? (
                                                    order.creator.avatar ? (
                                                        <div className="w-6 h-6 rounded-full overflow-hidden shrink-0 border border-slate-200 dark:border-slate-700">
                                                            <img src={order.creator.avatar} alt="Avatar" className="w-full h-full object-cover" />
                                                        </div>
                                                    ) : (
                                                        <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-black border border-primary/20 shrink-0">
                                                            {order.creator.name ? order.creator.name.charAt(0).toUpperCase() : '?'}
                                                        </div>
                                                    )
                                                ) : (
                                                    <span className="text-slate-400 italic text-xs">Système</span>
                                                )}
                                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300" title={order.creator?.name}>
                                                    {order.creator?.name || ''}
                                                </span>
                                            </div>
                                        </td>

                                        <td className="px-6 py-4 text-right font-black text-sm font-mono text-slate-700 dark:text-slate-300">
                                            {parseFloat(order.totalHT || 0).toFixed(2)} €
                                        </td>
                                        <PermissionGuard permission="order.view">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button onClick={(e) => { e.stopPropagation(); handleDownloadPDF(e, order.id, order.reference); }} className="p-2 text-slate-400 hover:text-primary transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" title="Télécharger PDF">
                                                        <span className="material-symbols-outlined text-lg">download</span>
                                                    </button>
                                                    <PermissionGuard permission="order.create">
                                                        <button
                                                            onClick={(e) => handleDuplicate(e, order.id)}
                                                            className="p-2 text-slate-400 hover:text-primary transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
                                                            title="Repasser la commande (Dupliquer)"
                                                        >
                                                            <span className="material-symbols-outlined text-lg">content_copy</span>
                                                        </button>
                                                    </PermissionGuard>
                                                    <PermissionGuard permission="order.create">
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleEditOrder(order); }}
                                                            disabled={!(order.status === 'Brouillon' || order.status === 'Envoyée')}
                                                            className={`p-2 rounded-lg transition-all ${(order.status === 'Brouillon' || order.status === 'Envoyée') ? 'text-amber-500 hover:bg-amber-500/10' : 'text-slate-300 dark:text-slate-600 opacity-50 cursor-not-allowed'}`}
                                                            title="Modifier"
                                                        >
                                                            <span className="material-symbols-outlined text-[20px]">edit</span>
                                                        </button>
                                                    </PermissionGuard>
                                                    <PermissionGuard permission="order.create">
                                                        <button
                                                            onClick={(e) => handleMarkDelivered(e, order.id)}
                                                            disabled={order.status !== 'Envoyée'}
                                                            className={`p-2 rounded-lg transition-all ${order.status === 'Envoyée' ? 'text-primary hover:bg-primary/10' : 'text-slate-300 dark:text-slate-600 opacity-50 cursor-not-allowed'}`}
                                                            title="Marquer Livrée"
                                                        >
                                                            <span className="material-symbols-outlined text-[20px]">local_shipping</span>
                                                        </button>
                                                    </PermissionGuard>
                                                    <PermissionGuard permission="order.view">
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleRowClick(order); }}
                                                            className="p-2 text-blue-500 hover:bg-blue-500/10 rounded-lg transition-all"
                                                            title={(order.status === 'Envoyée' || order.status === 'Partiellement Livrée') ? 'Réceptionner / Historique' : 'Voir détails & Historique'}
                                                        >
                                                            <span className="material-symbols-outlined text-[20px]">{(order.status === 'Envoyée' || order.status === 'Partiellement Livrée') ? 'inventory_2' : 'info'}</span>
                                                        </button>
                                                    </PermissionGuard>
                                                </div>
                                            </td>
                                        </PermissionGuard>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Area */}
                    <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        limit={limit}
                        onLimitChange={(l) => {
                            setLimit(l);
                            setPage(1);
                            localStorage.setItem('app_items_per_page', l);
                        }}
                        totalItems={total}
                    />
                </div >
            </div >

            {/* Modal de Sélection du Fournisseur Premium */}
            {
                isSupplierSelectModalOpen && (
                    <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
                        <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl shadow-primary/20 w-full max-w-xl flex flex-col max-h-[90vh] border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-500">
                            {/* Premium Header with Gradient */}
                            <div className="shrink-0 px-8 py-6 relative overflow-hidden bg-gradient-to-br from-primary/10 via-primary/5 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent border-b border-primary/10 dark:border-primary/20">
                                <div className="relative z-10 flex justify-between items-center">
                                    <div className="flex gap-4 items-center">
                                        <div className="size-12 rounded-2xl bg-white dark:bg-slate-800 flex items-center justify-center shadow-sm border border-primary/10">
                                            <span className="material-symbols-outlined text-primary text-[28px] font-bold">storefront</span>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-black text-slate-900 dark:text-white leading-tight">Sélection Fournisseur</h3>
                                            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em] opacity-70">Choisissez un partenaire pour la commande</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setIsSupplierSelectModalOpen(false)}
                                        className="size-10 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 shadow-sm hover:shadow transition-all"
                                    >
                                        <span className="material-symbols-outlined">close</span>
                                    </button>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto p-8 space-y-6">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium px-2 italic">
                                    Sélectionnez un fournisseur pour initialiser la commande d'achat et accéder au catalogue produits.
                                </p>
                                <div className="grid grid-cols-1 gap-3 p-4 bg-slate-50/50 dark:bg-slate-800/20 rounded-[2rem] border border-slate-100 dark:border-slate-800">
                                    {suppliersList.length === 0 ? (
                                        <div className="py-12 text-center">
                                            <span className="material-symbols-outlined text-4xl text-slate-300 mb-2">person_off</span>
                                            <p className="text-slate-400 font-bold text-sm italic">Aucun fournisseur configuré</p>
                                        </div>
                                    ) : suppliersList.map(s => (
                                        <button
                                            key={s.id}
                                            onClick={() => onSupplierSelected(s)}
                                            className="w-full group p-5 bg-white dark:bg-slate-800/40 rounded-2xl border border-transparent hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all flex justify-between items-center"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="size-10 rounded-xl bg-slate-50 dark:bg-slate-900 flex items-center justify-center text-slate-400 group-hover:text-primary transition-colors">
                                                    <span className="material-symbols-outlined">business</span>
                                                </div>
                                                <div className="text-left">
                                                    <p className="font-black text-slate-900 dark:text-white text-sm group-hover:text-primary transition-colors">{s.name}</p>
                                                    <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest">{s.category || 'Sans catégorie'}</p>
                                                </div>
                                            </div>
                                            <span className="material-symbols-outlined text-slate-300 group-hover:text-primary group-hover:translate-x-1 transition-all">chevron_right</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="p-6 px-8 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                                <button
                                    onClick={() => setIsSupplierSelectModalOpen(false)}
                                    className="px-8 py-3 rounded-xl text-slate-500 font-black text-[10px] uppercase tracking-[0.2em] hover:bg-white dark:hover:bg-slate-800 hover:shadow-sm border border-transparent hover:border-slate-200 transition-all"
                                >
                                    Abandonner
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                isEditModalOpen && (
                    <PurchaseOrderModal
                        isOpen={isEditModalOpen}
                        onClose={() => setIsEditModalOpen(false)}
                        onCreated={fetchOrders}
                        supplier={selectedSupplier}
                        catalogue={supplierCatalogue}
                        order={selectedOrder}
                    />
                )
            }

            {
                isReceptionModalOpen && selectedOrder && (
                    <PurchaseOrderReceptionModal
                        isOpen={isReceptionModalOpen}
                        onClose={() => setIsReceptionModalOpen(false)}
                        order={selectedOrder}
                        onUpdated={fetchOrders}
                    />
                )
            }
        </div >
    );
};

export default PurchaseOrderList;
